class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleBodyKeyup = (evt) => {
      if (evt.code === 'Escape') this.close();
    };

    this.handleOverlayClick = this.close.bind(this);

    this.addEventListener('keyup', this.handleBodyKeyup);

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.handleOverlayClick);
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    if (cartLink.dataset.drawerBound === 'true') return;

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    cartLink.dataset.drawerBound = 'true';
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    if (!parsedState.sections) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      if (section.id === 'cart-icon-bubble') {
        this.renderCartIconBubble(sectionHtml);
        return;
      }

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const newHtml = this.getSectionInnerHTML(sectionHtml, section.selector || '.shopify-section');
      if (newHtml !== null) {
        sectionElement.innerHTML = newHtml;
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.removeEventListener('click', this.handleOverlayClick);
        overlay.addEventListener('click', this.handleOverlayClick);
      }

      this.setHeaderCartIconAccessibility();
      this.open();
    });
  }

  renderCartIconBubble(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newBubble = parsed.querySelector('#cart-icon-bubble');
    const currentBubble = document.querySelector('#cart-icon-bubble');

    if (newBubble && currentBubble) {
      currentBubble.replaceWith(newBubble);
      this.setHeaderCartIconAccessibility();
      return;
    }

    fetch(`${window.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const bubble = document.querySelector('#cart-icon-bubble');
        if (!bubble) return;

        const countEl = bubble.querySelector('[data-cart-count]');
        const textEl = bubble.querySelector('[data-cart-count-text]');
        const bubbleWrap = bubble.querySelector('.cart-count-bubble');
        const iconWrap = bubble.querySelector('.cart-icon-wrapper');

        if (countEl) {
          countEl.textContent = cart.item_count > 99 ? '99+' : cart.item_count;
        }

        if (textEl) {
          textEl.textContent = `${cart.item_count} item(s) in cart`;
        }

        if (bubbleWrap) {
          bubbleWrap.classList.toggle('hidden', cart.item_count < 1);
        }

        if (iconWrap) {
          iconWrap.innerHTML =
            cart.item_count < 1
              ? window.cartIconEmptySvg || iconWrap.innerHTML
              : window.cartIconFullSvg || iconWrap.innerHTML;
        }

        this.setHeaderCartIconAccessibility();
      })
      .catch((error) => {
        console.error('Cart icon bubble fallback update failed:', error);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer .drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);